import { useEffect, useMemo, useRef, useState } from "react";
import { Button, PocketSection } from "@/components/ui";
import type { AvailableRuntime } from "@/stores/companion-store";
import type { RuntimeHealthIssue } from "@/desktop/connection-health/runtime-health";
import { resolveRuntimeActionMode } from "@/desktop/connection-health/runtime-action-mode";
import { requestRuntimeHealthAction } from "@/desktop/connection-health/runtime-action-events";
import type { RuntimeSetupMode } from "@/desktop/onboarding/runtime-setup-mode";
import { isPersonalRuntime } from "@/desktop/onboarding/post-demo-runtime-preference";
import { useT } from "@/i18n";
import { settingsConnectionsMessages } from "@/i18n/messages/settings-connections";

/** The owner window keeps the setup run alive after Settings closes. */
export function ConnectionToolAction({
  runtime,
}: {
  runtime: AvailableRuntime | undefined;
}) {
  const t = useT(settingsConnectionsMessages);
  const issue = useMemo<RuntimeHealthIssue | null>(() => {
    if (!runtime || !isPersonalRuntime(runtime)) return null;
    if (runtime.logged_in === false)
      return { kind: "signed_out", runtimeType: runtime.type, runtime };
    if (runtime.update_available && runtime.update_command)
      return { kind: "update", runtimeType: runtime.type, runtime };
    return null;
  }, [runtime]);
  const [mode, setMode] = useState<RuntimeSetupMode | null>(null);
  const [starting, setStarting] = useState(false);
  const [failed, setFailed] = useState(false);
  // The page re-checks the tool every few seconds and gets a fresh runtime
  // each time; only a different issue resolves the action again, so the
  // button and its message stay put instead of blinking out.
  const issueKey = issue && `${issue.kind}:${issue.runtimeType}`;
  const latestIssue = useRef(issue);
  useEffect(() => {
    latestIssue.current = issue;
  });
  useEffect(() => {
    let active = true;
    setMode(null);
    setFailed(false);
    const current = latestIssue.current;
    if (current)
      void resolveRuntimeActionMode(current).then((resolved) => {
        if (active) setMode(resolved);
      });
    return () => {
      active = false;
    };
  }, [issueKey]);

  if (!issue || !mode || mode === "copy") return null;
  const signIn = issue.kind === "signed_out";
  const label = signIn ? t("signIn") : t("update");
  async function start() {
    if (!issue || starting) return;
    setStarting(true);
    setFailed(false);
    try {
      await requestRuntimeHealthAction(issue);
    } catch {
      setFailed(true);
    } finally {
      setStarting(false);
    }
  }
  return (
    <PocketSection>
      <div className="space-y-2 px-2">
        <Button
          size="sm"
          data-testid="connection-tool-run-action"
          disabled={starting}
          onClick={() => void start()}
        >
          {starting ? t("starting") : label}
        </Button>
        {signIn && (
          <p className="text-appkit-small text-muted">
            {mode === "guided"
              ? t("signInContinueTerminal")
              : t("signInFinishBrowser")}
          </p>
        )}
        {failed && (
          <p role="alert" className="text-appkit-small text-danger">
            {signIn ? t("signInStartFailed") : t("updateStartFailed")}
          </p>
        )}
      </div>
    </PocketSection>
  );
}
